import { useEffect, useState } from 'react'

import { useUser } from '../context/UserContext'
import { createAdminApi } from '../services/adminApi'
import styles from './HomePage.module.css'

function formatRelative(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

export default function HomePage() {
  const { me, isLoggedIn, loading, sessions, logout, refreshSessions } = useUser()

  const [selectedCharacter, setSelectedCharacter] = useState('')
  const [selectedStory, setSelectedStory] = useState('')
  const [availableCharacters, setAvailableCharacters] = useState<{ name: string; version: number }[]>([])
  const [availableStories, setAvailableStories] = useState<{ sourceFilename: string; title: string; inDatabase: boolean; characterName: string }[]>([])

  useEffect(() => {
    if (!isLoggedIn) return
    const api = createAdminApi()
    api.getCharacters().then(setAvailableCharacters).catch(() => {})
    api.getStories()
      .then((all) => setAvailableStories(all.filter((s) => s.inDatabase)))
      .catch(() => {})
  }, [isLoggedIn])

  function startChat() {
    const params = new URLSearchParams()
    if (selectedCharacter) params.set('character', selectedCharacter)
    if (selectedStory) params.set('story', selectedStory)
    const query = params.toString()
    window.location.href = query ? `?${query}#chat` : '#chat'
  }

  // Unauthenticated splash — UserContext will already have triggered the
  // portal redirect on mount, so this is normally a single-frame flash.
  if (!isLoggedIn) {
    return (
      <div className={styles.loginPage}>
        <div className={styles.loginCard}>
          <h1 className={styles.loginTitle}>Convorcher</h1>
          <p className={styles.loginSubtitle}>
            {loading ? 'Checking session…' : 'Redirecting to sign in…'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.dashPage}>
      <header className={styles.dashHeader}>
        <h1>Home</h1>
        <div className={styles.authBar}>
          <span className={styles.authLabel}>Signed in as</span>
          <code className={styles.authKey}>{me?.email ?? me?.id ?? 'unknown'}</code>
          <button className={styles.disconnectBtn} onClick={logout}>Sign out</button>
        </div>
      </header>

      <div className={styles.dashContent}>
        <section className={styles.dashSection}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>New Chat</h2>
          </div>
          <div className={styles.newChatBody}>
            {availableCharacters.length > 0 && (
              <div className={styles.sessionParam}>
                <span className={styles.paramLabel}>Character</span>
                <select
                  value={selectedCharacter}
                  onChange={(e) => setSelectedCharacter(e.target.value)}
                  className={styles.paramSelect}
                >
                  {!selectedCharacter && <option value="" disabled>(server default)</option>}
                  {availableCharacters.map((c) => (
                    <option key={c.name} value={c.name}>{c.name} v{c.version}</option>
                  ))}
                </select>
              </div>
            )}
            {availableStories.length > 0 && (
              <div className={styles.sessionParam}>
                <span className={styles.paramLabel}>Story</span>
                <select
                  value={selectedStory}
                  onChange={(e) => setSelectedStory(e.target.value)}
                  className={styles.paramSelect}
                >
                  {!selectedStory && <option value="" disabled>(server default)</option>}
                  {availableStories.map((s) => (
                    <option key={s.sourceFilename} value={s.sourceFilename}>
                      {s.title || s.sourceFilename}{s.characterName ? ` (${s.characterName})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button className={styles.startBtn} onClick={startChat}>Start Chat</button>
          </div>
        </section>

        <section className={styles.dashSection}>
          <div className={styles.sectionHead}>
            <h2 className={styles.sectionTitle}>Active Sessions</h2>
            <button className={styles.refreshBtn} onClick={refreshSessions}>Refresh</button>
          </div>
          {sessions.length === 0 ? (
            <p className={styles.empty}>No active sessions yet.</p>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Character</th>
                  <th>Session ID</th>
                  <th>Story</th>
                  <th>Last Active</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => (
                  <tr key={s.id}>
                    <td className={styles.charCell}>{s.character}</td>
                    <td><code className={styles.idCell}>{s.id.slice(0, 8)}…</code></td>
                    <td className={styles.storyCell}>{s.storyPath.replace('stories/', '').replace('.yaml', '')}</td>
                    <td className={styles.timeCell}>{formatRelative(s.lastActive)}</td>
                    <td><a href="#chat" className={styles.openLink}>Open</a></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </div>
  )
}
